import React, { useState, useEffect } from 'react';
import { ALL_COUNTRIES } from '../constants/countries';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../services/firebaseConfig';


const Applicants = ({ 
  user, 
  settings, 
  applicants = [], 
  applications = [], 
  onSaveApplicant, 
  onDeleteApplicant,
  viewOnly = false, // Step 4 Injection: Prop used to hide restricted actions
  users = []
  }) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // --- NEW: Local State for Instant UI Feedback ---
  const [localApplicants, setLocalApplicants] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sync props to local state ONLY if we are not in the middle of a delete operation
  // This prevents the "old" list from the database re-appearing before the delete syncs.
  useEffect(() => {
    if (Array.isArray(applicants) && !isDeleting) {
      setLocalApplicants(applicants);
    }
  }, [applicants, isDeleting]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    citizenship: '',
    gender: 'Unspecified',
    dob: '',
    email: '',
    phone: '',
    address: '',
    passport: null,
    aadhar: null
  });

  const apiKey = "AIzaSyB4TvfH7qZxEiaMBPntaj9dh8tu-atp7u0"; 

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  /**
   * Helper: Firebase Storage File Uploader for Identity Docs
   */
  const uploadIdentityFile = async (file, applicantEmail, type) => {
    if (!file || !(file instanceof File)) return null;
    try {
      const storageRef = ref(storage, `applicants/${applicantEmail}/${type}_${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      return await getDownloadURL(snapshot.ref);
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      return null;
    }
  };

  /* src/components/Applicants.js */

  /* src/components/Applicants.js */

  const analyzeDocument = async (file, type) => {
    if (!file) return;
    setIsAiProcessing(true);
    try {
      const base64Data = await fileToBase64(file);
      const mimeType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');

      const prompt = `Extract details from this ${type} document. 
        Return strictly a JSON object with these keys: 
        "firstName", "lastName", "dob" (format YYYY-MM-DD), "gender" ("Male", "Female", or "Unspecified"), "citizenship", "address".
        Output only the JSON, no markdown code blocks, no preamble, and no extra text.`;

      const payload = {
        contents: [{
          parts: [
            { text: prompt }, 
            { inlineData: { mimeType: mimeType, data: base64Data } }
          ]
        }],
        generationConfig: {
          temperature: 0.1,
          maxOutputTokens: 1000,
        }
      };

      // FIXED URL: Using the exact model from your GeminiAssistant (gemini-2.0-flash)
      // and ensuring the correct v1beta path structure
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorBody = await response.json();
        console.error("Gemini API Error Detail:", errorBody);
        throw new Error(`Status ${response.status}: ${errorBody.error?.message || 'Model Request Failed'}`);
      }

      const result = await response.json();
      
      let rawText = result.candidates?.[0]?.content?.parts?.[0]?.text || "{}";
      const cleanedJson = rawText.replace(/```json/g, "").replace(/```/g, "").trim();
      
      let extractedData;
      try {
        extractedData = JSON.parse(cleanedJson);
      } catch (parseErr) {
        console.error("Failed to parse AI response:", cleanedJson);
        throw new Error("AI returned data in an unreadable format.");
      }
      
      setFormData(prev => ({
        ...prev,
        firstName: extractedData.firstName || prev.firstName,
        lastName: extractedData.lastName || prev.lastName,
        dob: extractedData.dob || prev.dob,
        gender: (extractedData.gender === "Male" || extractedData.gender === "Female") 
                ? extractedData.gender 
                : prev.gender,
        citizenship: extractedData.citizenship || prev.citizenship,
        address: extractedData.address || prev.address,
        [type.toLowerCase()]: file 
      }));

      alert(`${type} analyzed successfully! Form fields have been updated.`);

    } catch (error) {
      console.error("AI Analysis Failed:", error);
      alert("AI Analysis Error: " + error.message);
    } finally {
      setIsAiProcessing(false);
    }
  };

  const handleCreateApplicant = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.citizenship || formData.citizenship === "") {
      alert("Please select a Citizenship.");
      return;
    }

    if (!formData.gender || formData.gender === "Unspecified") {
      alert("Please select a Gender.");
      return;
    }

    const isDuplicate = applicants.some(app => {
      if (isEditing && app.id === formData.id) return false;
      const emailMatch = app.email?.toLowerCase() === formData.email?.toLowerCase();
      const phoneMatch = app.phone === formData.phone;
      return emailMatch || phoneMatch;
    });

    if (isDuplicate) {
      alert("Validation Error: An applicant with this Email or Phone already exists.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // 1. Identify the current user's organization name for tagging
      const currentUserData = (users || []).find(u => String(u.uid) === String(user?.uid));

      // 2. Await file uploads to Storage
      const passportUrl = await uploadIdentityFile(formData.passport, formData.email, 'passport');
      const aadharUrl = await uploadIdentityFile(formData.aadhar, formData.email, 'aadhar');

      if (onSaveApplicant) {
        const targetId = isEditing ? formData.id : `ALM-${Date.now()}`;

        const cleanData = { 
          ...formData,
          agentId: user?.uid,
          // ADD THIS LINE: Tags the record with the agency name (e.g., "Agent ABC")
          agentName: currentUserData?.agentAssociation || '',
          updatedAt: new Date().toISOString(),
          createdAt: isEditing ? (formData.createdAt || new Date().toISOString()) : new Date().toISOString(),
          id: targetId,
          passportUrl: passportUrl || formData.passportUrl || null,
          aadharUrl: aadharUrl || formData.aadharUrl || null
        };
        
        // Remove raw file objects
        delete cleanData.passport;
        delete cleanData.aadhar;
        
        if (isEditing) {
          setLocalApplicants(prev => prev.map(a => a.id === targetId ? cleanData : a));
          if (selectedApplicant && selectedApplicant.id === targetId) {
            setSelectedApplicant(cleanData);
          }
        } else {
          setLocalApplicants(prev => [cleanData, ...prev]);
        }

        await onSaveApplicant(cleanData, targetId); 
      }

      setShowForm(false);
      setIsEditing(false);
      setFormData({
        firstName: '', lastName: '', citizenship: '', gender: 'Unspecified',
        dob: '', email: '', phone: '', address: '', passport: null, aadhar: null
      });
    } catch (error) {
      console.error("Failed to save applicant record:", error);
      alert("Error saving record: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * handleDeleteClick
   * 1. Stops event bubbling.
   * 2. Checks permissions/links.
   * 3. Locks sync state (isDeleting).
   * 4. Updates UI instantly.
   * 5. Sends delete command.
   */
  const handleDeleteClick = async (e, applicantId) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!applicantId) {
      alert("System Error: Applicant ID is missing.");
      return;
    }

    const hasLinkedApplications = (applications || []).some(
      (app) => String(app.applicantId) === String(applicantId)
    );

    if (hasLinkedApplications) {
      alert("Action Blocked: You cannot delete this applicant because they have active applications saved in the system.");
      return;
    }

    if (window.confirm("Are you sure you want to permanently delete this applicant record?")) {
      // LOCK SYNC & SNAPSHOT STATE
      setIsDeleting(true);
      const previousApplicants = [...localApplicants];

      // OPTIMISTIC UPDATE
      setLocalApplicants(prev => prev.filter(app => app.id !== applicantId));
      if (selectedApplicant?.id === applicantId) {
        setSelectedApplicant(null);
      }

      try {
        if (typeof onDeleteApplicant === 'function') {
          await onDeleteApplicant(applicantId);
          // Keep lock briefly to ensure DB catches up
          setTimeout(() => setIsDeleting(false), 2000);
        } else {
          throw new Error("Delete function not connected");
        }
      } catch (error) {
        console.error("Delete Failed:", error);
        
        // REVERT ON FAILURE
        setLocalApplicants(previousApplicants);
        setIsDeleting(false);

        if (error.code === 'permission-denied') {
          alert("Permission Denied: You do not have permission to delete this record.");
        } else {
          alert(`Delete Failed: ${error.message}`);
        }
      }
    }
  };

  const renderApplicantDetail = () => (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 space-y-8">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setSelectedApplicant(null)}
            className="w-10 h-10 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0464AD] transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h2 className="text-3xl font-black text-slate-900 tracking-tighter leading-none uppercase">
              {selectedApplicant.firstName} {selectedApplicant.lastName}
            </h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Applicant Profile Summary</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2rem] border border-slate-100 p-8 shadow-sm space-y-8">
            
            {/* 1. TEXT DATA GRID (Direct JSX - No more DetailBlock errors) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { label: "Citizenship", value: selectedApplicant.citizenship },
                { label: "Gender", value: selectedApplicant.gender },
                { label: "Date of Birth", value: selectedApplicant.dob },
                { label: "Email Address", value: selectedApplicant.email },
                { label: "Phone Number", value: selectedApplicant.phone },
                { label: "Address", value: selectedApplicant.address }
              ].map((item, i) => (
                <div key={i} className="space-y-1">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{item.label}</p>
                  <p className="text-sm font-bold text-slate-800">{item.value || '—'}</p>
                </div>
              ))}
            </div>

            {/* 2. IDENTITY DOCUMENTS SECTION */}
            <div className="pt-8 border-t border-slate-50">
              <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-4">Identity Documents</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Passport Card */}
                {selectedApplicant.passportUrl ? (
                  <a 
                    href={selectedApplicant.passportUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl hover:border-[#0464AD] hover:bg-white transition-all shadow-sm group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">📄</span>
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase leading-none mb-1">Passport Copy</p>
                        <p className="text-xs font-bold text-[#0464AD] group-hover:underline">View Document</p>
                      </div>
                    </div>
                  </a>
                ) : (
                  <div className="p-4 bg-slate-50/50 border border-dashed border-slate-200 rounded-2xl flex items-center gap-3 grayscale opacity-50">
                    <span className="text-xl">📄</span>
                    <p className="text-[10px] font-bold text-slate-400 uppercase italic">No Passport uploaded</p>
                  </div>
                )}

                {/* Aadhar Card */}
                {selectedApplicant.aadharUrl ? (
                  <a 
                    href={selectedApplicant.aadharUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl hover:border-[#0464AD] hover:bg-white transition-all shadow-sm group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">🪪</span>
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase leading-none mb-1">Aadhar Card</p>
                        <p className="text-xs font-bold text-[#0464AD] group-hover:underline">View Document</p>
                      </div>
                    </div>
                  </a>
                ) : (
                  <div className="p-4 bg-slate-50/50 border border-dashed border-slate-200 rounded-2xl flex items-center gap-3 grayscale opacity-50">
                    <span className="text-xl">🪪</span>
                    <p className="text-[10px] font-bold text-slate-400 uppercase italic">No Aadhar uploaded</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );


  const renderApplicants = () => (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">Active Applicants</h3>
        {/* Step 4 Injection: Hide button if viewOnly */}
        {!viewOnly && (
          <button 
            onClick={() => {
              setIsEditing(false);
              setFormData({
                firstName: '', lastName: '', citizenship: '', gender: 'Unspecified',
                dob: '', email: '', phone: '', address: '', passport: null, aadhar: null
              });
              setShowForm(true);
            }}
            className="bg-[#0464AD] text-white px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95"
          >
            Create Applicant
          </button>
        )}
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden min-h-[400px]">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Name</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Email</th>
              {/* NEW HEADERS */}
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Phone</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date of Birth</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {localApplicants.length > 0 ? (
              localApplicants.map((app, index) => (
                <tr key={app.id || index} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-8 py-4 font-bold text-slate-800">{app.firstName} {app.lastName}</td>
                  <td className="px-8 py-4 text-slate-600 font-medium">{app.email}</td>
                  <td className="px-8 py-4 text-slate-600 font-medium">{app.phone || '—'}</td>
                  <td className="px-8 py-4 text-slate-600 font-medium">{app.dob || '—'}</td>
                  <td className="px-8 py-4 text-right">

                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => setSelectedApplicant(app)}
                        className="p-2 text-slate-300 hover:text-[#0464AD] hover:bg-blue-50 rounded-lg transition-all"
                        title="View Full Profile"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-1.274 0-2.484-.23-3.605-.65M3 3l18 18" />
                        </svg>
                      </button>
                      
                      {/* Step 4 Injection: Hide Edit/Delete if viewOnly */}
                      {!viewOnly && (
                        <>
                          <button 
                            onClick={() => {
                              setFormData({ ...app, passport: null, aadhar: null });
                              setIsEditing(true);
                              setShowForm(true);
                            }}
                            className="p-2 text-slate-300 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all"
                            title="Edit Profile"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M15.172 2.172a2.828 2.828 0 114 4L11.828 15H9v-2.828l6.172-6.172z" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => handleDeleteClick(e, app.id)}
                            className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete Applicant"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-8 py-16 text-center italic text-slate-400">No applicants found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="px-10 py-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
              <div>
                <h3 className="text-2xl font-black text-slate-900 tracking-tighter">
                  {isEditing ? 'Update Applicant' : 'New Applicant'}
                </h3>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Personal & Contact Registration</p>
              </div>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-red-500 transition-colors">✕</button>
            </div>

            <form onSubmit={handleCreateApplicant} className="p-10 space-y-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    Passport Copy <span className="text-[8px] bg-[#0464AD] text-white px-1.5 py-0.5 rounded">AI AUTO-FILL</span>
                  </label>
                  <div className="relative group">
                    <input type="file" className="hidden" id="passport-upload" accept="image/jpeg,image/png,image/jpg,application/pdf" onChange={e => e.target.files[0] && analyzeDocument(e.target.files[0], 'Passport')} />
                    <label htmlFor="passport-upload" className={`flex items-center justify-between w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm cursor-pointer hover:border-[#0464AD] transition-all ${isAiProcessing ? 'opacity-50 cursor-wait' : ''}`}>
                      <span className="text-slate-500 truncate">{formData.passport ? formData.passport.name : (isEditing ? 'Update Passport (PDF/JPG/PNG)...' : 'Upload Image or PDF...') }</span>
                      <svg className="w-5 h-5 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                    </label>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    Aadhar Card <span className="text-[8px] bg-[#0464AD] text-white px-1.5 py-0.5 rounded">AI AUTO-FILL</span>
                  </label>
                  <div className="relative group">
                    <input type="file" className="hidden" id="aadhar-upload" accept="image/jpeg,image/png,image/jpg,application/pdf" onChange={e => e.target.files[0] && analyzeDocument(e.target.files[0], 'Aadhar')} />
                    <label htmlFor="aadhar-upload" className={`flex items-center justify-between w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm cursor-pointer hover:border-[#0464AD] transition-all ${isAiProcessing ? 'opacity-50 cursor-wait' : ''}`}>
                      <span className="text-slate-500 truncate">{formData.aadhar ? formData.aadhar.name : (isEditing ? 'Update Aadhar (PDF/JPG/PNG)...' : 'Upload Image or PDF...') }</span>
                      <svg className="w-5 h-5 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                  <input type="text" required className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm focus:ring-2 focus:ring-[#0464AD] outline-none" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                  <input type="text" required className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm focus:ring-2 focus:ring-[#0464AD] outline-none" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Citizenship</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none cursor-pointer" 
                    value={formData.citizenship} 
                    onChange={e => setFormData({...formData, citizenship: e.target.value})} 
                    required
                  >
                    <option value="">Select Citizenship...</option>
                    {ALL_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Gender</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none cursor-pointer" 
                    value={formData.gender} 
                    onChange={e => setFormData({...formData, gender: e.target.value})}
                    required  
                  >
                    <option value="">Select Gender...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Unspecified">Unspecified</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Date of Birth</label>
                  <input type="date" required className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm focus:ring-2 focus:ring-[#0464AD] outline-none" value={formData.dob} onChange={e => setFormData({...formData, dob: e.target.value})} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                  <input type="email" required className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm focus:ring-2 focus:ring-[#0464AD] outline-none" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Phone Number</label>
                <input 
                  type="tel" 
                  required 
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm focus:ring-2 focus:ring-[#0464AD] outline-none" 
                  value={formData.phone} 
                  onChange={e => setFormData({...formData, phone: e.target.value})} 
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Postal Address</label>
                <textarea rows="3" required className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm focus:ring-2 focus:ring-[#0464AD] outline-none resize-none" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
              </div>

              <div className="pt-6 flex gap-4">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800 transition-colors">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="flex-1 bg-[#0464AD] text-white py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl active:scale-95 disabled:opacity-50">
                  {isSubmitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Record')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return selectedApplicant ? renderApplicantDetail() : renderApplicants();
};

export default Applicants;